/* Match access panel (admin match page): server connect info, coach tablet links and the
 * live log stream in one card with three columns. Owner / super admin only — the caller
 * decides which columns show (same rules as the old separate cards).
 * Secrets (IP middle octets, server password, tokens) are masked until the column's eye is
 * clicked; Copy always copies the full value. Regenerate is confirm-before-action. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './matchAccess.css';

const DOT = '•';
const maskToken = (tok) => (tok ? DOT.repeat(Math.min(12, tok.length)) : '');

// 62.12.34.184 → 62.***.***.184 (first and last part stay visible)
export function maskHost(host) {
  const parts = String(host || '').split('.');
  if (parts.length < 3) return host;
  return parts.map((p, i) => (i === 0 || i === parts.length - 1 ? p : '***')).join('.');
}

/* ── icons ── */
const Svg = ({ size = 14, sw = 2, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IconServer = () => <Svg><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /><path d="M7 7.5h.01M7 16.5h.01" /></Svg>;
const IconTimer = () => <Svg><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2" /><path d="M9 2h6" /></Svg>;
const IconStream = () => <Svg><path d="M4 11a9 9 0 0 1 9 9" /><path d="M4 4a16 16 0 0 1 16 16" /><circle cx="5" cy="19" r="1" /></Svg>;
const IconShield = () => <Svg size={15}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></Svg>;
const IconEye = ({ open }) => (
  <Svg size={13}>
    {open
      ? (<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>)
      : (<><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></>)}
  </Svg>
);
const IconCopy = () => <Svg size={12}><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Svg>;
const IconCheck = () => <Svg size={12} sw={3}><polyline points="20 6 9 17 4 12" /></Svg>;
const IconRegen = () => <Svg size={12}><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></Svg>;
const IconOpen = () => <Svg size={12}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></Svg>;
const IconInfo = () => <Svg size={12}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></Svg>;

/* ── shared pieces ── */
function useCopy() {
  const [copied, setCopied] = useState(null);
  const copy = useCallback((text, key) => {
    try { navigator.clipboard.writeText(text); } catch (_) { /* clipboard blocked */ }
    setCopied(key);
    setTimeout(() => setCopied(c => (c === key ? null : c)), 1800);
  }, []);
  return [copied, copy];
}

function CopyBtn({ done, onClick }) {
  const { t } = useTranslation();
  return (
    <button type="button" className={`ma-copy${done ? ' done' : ''}`} onClick={onClick} title={t('matchAccess.copy', 'Copy')}>
      {done ? <IconCheck /> : <IconCopy />}
      <span>{done ? t('matchAccess.copied', 'Copied') : t('matchAccess.copy', 'Copy')}</span>
    </button>
  );
}

function EyeBtn({ open, onClick }) {
  const { t } = useTranslation();
  return (
    <button type="button" className={`ma-icon-btn${open ? ' on' : ''}`} onClick={onClick}
      title={open ? t('matchAccess.hideSecrets', 'Hide') : t('matchAccess.showSecrets', 'Reveal')}
      aria-label={open ? t('matchAccess.hideSecrets', 'Hide') : t('matchAccess.showSecrets', 'Reveal')} aria-pressed={open}>
      <IconEye open={open} />
    </button>
  );
}

function Row({ label, labelTitle, dot, value, copyValue, copyKey, copied, onCopy }) {
  return (
    <div className="ma-row">
      <div className="ma-row-main">
        <span className="ma-row-label" title={labelTitle || undefined}>
          {dot && <i className="ma-dot" style={{ background: dot, boxShadow: `0 0 8px ${dot}88` }} />}
          {label}
        </span>
        <span className="ma-row-value">{value}</span>
      </div>
      <CopyBtn done={copied === copyKey} onClick={() => onCopy(copyValue, copyKey)} />
    </div>
  );
}

/** Regenerate with a confirm step (old links stop working). */
function RegenControl({ confirming, busy, disabled, onAsk, onConfirm, onCancel }) {
  const { t } = useTranslation();
  if (!confirming) {
    return (
      <button type="button" className="ma-icon-btn warn" onClick={onAsk} disabled={disabled} title={t('matchAccess.regenerate', 'Regenerate')} aria-label={t('matchAccess.regenerate', 'Regenerate')}>
        <IconRegen />
      </button>
    );
  }
  return (
    <span className="ma-confirm">
      <button type="button" className="ma-pill danger" onClick={onConfirm} disabled={busy}>{busy ? '…' : t('matchAccess.confirm', 'Confirm')}</button>
      <button type="button" className="ma-pill" onClick={onCancel}>{t('common.cancel', 'Cancel')}</button>
    </span>
  );
}

function Col({ icon, title, meta, actions, accent, warning, children }) {
  return (
    <div className="ma-col" style={{ '--ma-accent': accent }}>
      <div className="ma-col-head">
        <span className="ma-col-icon">{icon}</span>
        <span className="ma-col-title">{title}</span>
        {meta}
        <span className="ma-col-actions">{actions}</span>
      </div>
      {warning && <div className="ma-warning">{warning}</div>}
      <div className="ma-col-body">{children}</div>
    </div>
  );
}

function Loading({ error, onRetry }) {
  const { t } = useTranslation();
  if (error) {
    return (
      <div className="ma-state error">
        {t('matchAccess.loadError', 'Could not load.')}
        <button type="button" className="ma-pill" onClick={onRetry}>{t('matchAccess.retry', 'Retry')}</button>
      </div>
    );
  }
  return <div className="ma-state"><i className="ma-spin" />{t('matchAccess.loading', 'Loading…')}</div>;
}

/* ── columns ── */
function ServerColumn({ match, displayIp }) {
  const { t } = useTranslation();
  const [reveal, setReveal] = useState(false);
  const [copied, copy] = useCopy();
  const port = match.server_port || '27015';
  const pw = match.server_password;
  const pwPart = (shown) => (pw ? `;password ${shown ? pw : DOT.repeat(8)}` : '');
  const connectFull = `connect ${displayIp}:${port}${pwPart(true)}`;
  const connectShown = `connect ${reveal ? displayIp : maskHost(displayIp)}:${port}${pwPart(reveal)}`;
  const sdrFull = match.sdr_address ? `connect ${match.sdr_address}${pwPart(true)}` : '';
  const sdrShown = match.sdr_address ? `connect ${match.sdr_address}${pwPart(reveal)}` : '';
  const meta = match.server_name ? (
    <span className="ma-col-meta" title={match.server_country_code ? `${match.server_city ? match.server_city + ', ' : ''}${match.server_country_name || match.server_country_code}` : undefined}>
      {match.server_country_code && (
        <img src={`https://flagcdn.com/16x12/${match.server_country_code.toLowerCase()}.png`} alt={match.server_country_code} width="16" height="12" onError={e => { e.target.style.display = 'none'; }} />
      )}
      <span className="ma-ellipsis">{match.server_name}</span>
    </span>
  ) : null;
  return (
    <Col icon={<IconServer />} title={t('matchAccess.server', 'Server')} accent="#60a5fa" meta={meta}
      actions={<EyeBtn open={reveal} onClick={() => setReveal(v => !v)} />}>
      <Row label={t('matchAccess.connect', 'Connect')} value={connectShown} copyValue={connectFull} copyKey="ip" copied={copied} onCopy={copy} />
      {match.sdr_address && (
        <Row label={t('matchAccess.sdr', 'SDR')} labelTitle={t('matchAccess.sdrTitle', 'Valve SDR address (Steam Datagram Relay) - changes on server restart / map change')}
          value={sdrShown} copyValue={sdrFull} copyKey="sdr" copied={copied} onCopy={copy} />
      )}
    </Col>
  );
}

function CoachColumn({ matchId }) {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState(null);
  const [error, setError] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, copy] = useCopy();

  const load = useCallback(async () => {
    setError(false);
    try { setTokens((await axios.get(`/api/matches/${matchId}/timeout-tokens`)).data); } catch (_) { setError(true); }
  }, [matchId]);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`/api/matches/${matchId}/timeout-tokens/regenerate`);
      setTokens(prev => ({ ...prev, ...res.data }));
      setConfirming(false);
    } catch (_) { /* keep the old links on failure */ }
    setBusy(false);
  };

  const base = `${window.location.origin}/timeout/`;
  return (
    <Col icon={<IconTimer />} title={t('matchAccess.coachTitle', 'Coach tablets')} accent="#a5b4fc"
      warning={confirming ? t('matchAccess.coachRegenWarning', 'Old links will be invalidated. Coaches must reload their tablets.') : null}
      actions={<>
        <EyeBtn open={reveal} onClick={() => setReveal(v => !v)} />
        <RegenControl confirming={confirming} busy={busy} disabled={!tokens} onAsk={() => setConfirming(true)} onConfirm={regenerate} onCancel={() => setConfirming(false)} />
      </>}>
      {tokens ? [
        { key: 'team1', name: tokens.team1_name || t('matchAccess.teamFallback', 'Team {{n}}', { n: 1 }), token: tokens.timeout_token_team1, color: '#22d3ee' },
        { key: 'team2', name: tokens.team2_name || t('matchAccess.teamFallback', 'Team {{n}}', { n: 2 }), token: tokens.timeout_token_team2, color: '#f97316' },
      ].map(r => (
        <Row key={r.key} label={r.name} dot={r.color} value={base + (reveal ? r.token : maskToken(r.token))}
          copyValue={base + r.token} copyKey={r.key} copied={copied} onCopy={copy} />
      )) : <Loading error={error} onRetry={load} />}
      <div className="ma-hint">{t('matchAccess.coachHint', 'Each coach opens their link on a tablet to call tactical timeouts.')}</div>
    </Col>
  );
}

function StreamColumn({ matchId }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, copy] = useCopy();

  const load = useCallback(async () => {
    setError(false);
    try { setInfo((await axios.get(`/api/matches/${matchId}/log-stream-token`)).data); } catch (_) { setError(true); }
  }, [matchId]);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setBusy(true);
    try {
      setInfo((await axios.post(`/api/matches/${matchId}/log-stream-token/regenerate`)).data);
      setConfirming(false);
    } catch (_) { /* keep the old token on failure */ }
    setBusy(false);
  };

  const tok = info ? info.log_stream_token : '';
  const shownTok = reveal ? tok : maskToken(tok);
  const url = (x) => `${window.location.origin}/api/matches/${matchId}/log-stream?token=${x}`;
  const curl = (x) => `curl -N -H "Authorization: Bearer ${x}" ${window.location.origin}/api/matches/${matchId}/log-stream`;
  const meta = info ? (
    <span className={`ma-badge${info.clients ? ' live' : ''}`}>
      {t('matchAccess.connected', '{{count}} connected', { count: info.clients || 0 })}
    </span>
  ) : null;
  return (
    <Col icon={<IconStream />} title={t('matchAccess.streamTitle', 'Log stream')} accent="#2dd4bf" meta={meta}
      warning={confirming ? t('matchAccess.streamRegenWarning', 'The old link stops working and connected tools are disconnected.') : null}
      actions={<>
        <button type="button" className="ma-text-btn" onClick={() => window.open(`/admin/matches/${matchId}/live-logs`, '_blank', 'noopener')}>
          <IconOpen />{t('matchAccess.openPreview', 'Open live preview')}
        </button>
        <EyeBtn open={reveal} onClick={() => setReveal(v => !v)} />
        <RegenControl confirming={confirming} busy={busy} disabled={!info} onAsk={() => setConfirming(true)} onConfirm={regenerate} onCancel={() => setConfirming(false)} />
      </>}>
      {info ? (<>
        <Row label={t('matchAccess.streamUrl', 'URL')} value={url(shownTok)} copyValue={url(tok)} copyKey="url" copied={copied} onCopy={copy} />
        <Row label="curl" value={curl(shownTok)} copyValue={curl(tok)} copyKey="curl" copied={copied} onCopy={copy} />
      </>) : <Loading error={error} onRetry={load} />}
      <div className="ma-hint" title={t('matchDetail.logStreamDescription', 'Every raw server log line for this match, streamed as Server-Sent Events. Sends the last 200 lines on connect (?history=N, max 2000) and resumes from Last-Event-ID. Keep the token secret - anyone with it can read the logs.')}>
        <IconInfo />{t('matchAccess.streamHint', 'Raw server logs as Server-Sent Events. Keep the token secret.')}
      </div>
    </Col>
  );
}

const COLLAPSE_KEY = 'fluxbot_match_access_collapsed';

export default function MatchAccessPanel({ match, matchId, displayIp, showServer, showCoach, showStream }) {
  const { t } = useTranslation();
  // Hidden = header only (remembered in this browser); the columns aren't mounted, so nothing is fetched
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch (_) { return false; } });
  const toggle = () => setCollapsed(c => {
    try { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1'); } catch (_) { /* storage blocked */ }
    return !c;
  });
  if (!showServer && !showCoach && !showStream) return null;
  return (
    <section className={`ma-panel${collapsed ? ' collapsed' : ''}`} aria-label={t('matchAccess.title', 'Match access')}>
      <header className="ma-head">
        <span className="ma-head-icon"><IconShield /></span>
        <span className="ma-head-title">{t('matchAccess.title', 'Match access')}</span>
        <span className="ma-head-sub">{t('matchAccess.subtitle', 'Connect info and private links for players, coaches and tools')}</span>
        <button type="button" className="ma-text-btn ma-collapse" onClick={toggle} aria-expanded={!collapsed}>
          <IconEye open={collapsed} />{collapsed ? t('matchAccess.showCard', 'Show') : t('matchAccess.hideCard', 'Hide')}
        </button>
      </header>
      {!collapsed && <div className="ma-cols">
        {showServer && <ServerColumn match={match} displayIp={displayIp} />}
        {showCoach && <CoachColumn matchId={matchId} />}
        {showStream && <StreamColumn matchId={matchId} />}
      </div>}
    </section>
  );
}
